import { Controller } from "@hotwired/stimulus"

// Adds .is-scrolled to the sticky marketing header once the page moves, which
// brings in its hairline border and shadow. Also drives the hamburger menu
// that appears below the 768px breakpoint where the nav links move inline,
// and the desktop mega-dropdowns ("Features", "Free tools": hover-intent
// open, click toggle, outside-click/Escape close). Each dropdown is a
// .mk-nav-item holding one .mk-nav-trigger; opening one closes the others.
export default class extends Controller {
  static targets = ["navItem"]

  connect() {
    this.update = () => this.element.classList.toggle("is-scrolled", window.scrollY > 8)
    window.addEventListener("scroll", this.update, { passive: true })
    this.update()

    this._onOutsideClick = (event) => {
      this.navItemTargets.forEach((item) => { if (!item.contains(event.target)) this.#close(item) })
    }
    this._onEscape = (event) => {
      if (event.key === "Escape") { this.closeFeatures(); this.closeMenu() }
    }
    document.addEventListener("click", this._onOutsideClick)
    document.addEventListener("keydown", this._onEscape)
  }

  disconnect() {
    window.removeEventListener("scroll", this.update)
    document.removeEventListener("click", this._onOutsideClick)
    document.removeEventListener("keydown", this._onEscape)
    clearTimeout(this._closeTimer)
  }

  toggleMenu(event) {
    this.#setMenuOpen(!this.element.classList.contains("mk-header-menu-open"), event.currentTarget)
  }

  closeMenu() {
    this.#setMenuOpen(false, this.element.querySelector(".mk-header-toggle"))
  }

  // Mobile accordion row (the "Features" group): the body animates to its own
  // scrollHeight so it needs no fixed height in the CSS.
  toggleSection(event) {
    const button = event.currentTarget
    const section = button.closest(".mk-macc")
    const body = section.querySelector(".mk-macc-body")
    const open = section.classList.toggle("is-open")
    button.setAttribute("aria-expanded", open)
    body.style.maxHeight = open ? `${body.scrollHeight}px` : "0px"
  }

  #setMenuOpen(open, toggle) {
    this.element.classList.toggle("mk-header-menu-open", open)
    toggle?.setAttribute("aria-expanded", open)
    toggle?.setAttribute("aria-label", open ? "Close menu" : "Open menu")
  }

  // The dropdown methods keep their original names; each acts on the .mk-nav-item the event came from.
  toggleFeatures(event) {
    event.stopPropagation()
    const item = this.#itemFor(event)
    item.classList.contains("is-open") ? this.#close(item) : this.openFeatures(event)
  }

  openFeatures(event) {
    clearTimeout(this._closeTimer)
    const item = this.#itemFor(event)
    this.navItemTargets.forEach((other) => { if (other !== item) this.#close(other) })
    item.classList.add("is-open")
    item.querySelector(".mk-nav-trigger").setAttribute("aria-expanded", "true")
  }

  // A short delay (rather than closing the instant the pointer leaves the
  // trigger) gives the cursor time to travel diagonally down into the panel
  // without the dropdown vanishing underneath it.
  scheduleCloseFeatures(event) {
    clearTimeout(this._closeTimer)
    const item = this.#itemFor(event)
    this._closeTimer = setTimeout(() => this.#close(item), 200)
  }

  closeFeatures() {
    this.navItemTargets.forEach((item) => this.#close(item))
  }

  #itemFor(event) {
    return event.currentTarget.closest(".mk-nav-item")
  }

  #close(item) {
    item.classList.remove("is-open")
    item.querySelector(".mk-nav-trigger")?.setAttribute("aria-expanded", "false")
  }
}
